import React, { useState, useMemo } from 'react';
import { UnifiedSlot } from '../InstructorDashboard';

interface DashboardMonthViewProps {
  slots: UnifiedSlot[];
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  onDayClick: (date: string) => void;
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_PRIORITY: Record<string, number> = {
  conducted: 4,
  scheduled: 3,
  'pre-scheduled': 2,
  planned: 1,
  cancelled: 0,
};

function getDayStatusColor(daySlots: UnifiedSlot[]): string {
  if (daySlots.length === 0) return '';

  const nonCancelled = daySlots.filter(s => s.status !== 'cancelled');
  if (nonCancelled.length === 0) return 'day-cancelled';

  const allConducted = nonCancelled.every(s => s.status === 'conducted');
  if (allConducted) return 'day-conducted';

  const hasScheduled = nonCancelled.some(s => s.status === 'scheduled' || s.status === 'pre-scheduled');
  if (hasScheduled) return 'day-scheduled';

  return 'day-planned';
}

const DashboardMonthView: React.FC<DashboardMonthViewProps> = ({
  slots,
  currentMonth,
  onMonthChange,
  onDayClick,
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);

  // Slot-by-date map
  const slotsByDate = useMemo(() => {
    const map = new Map<string, UnifiedSlot[]>();
    for (const slot of slots) {
      if (!slot.date) continue;
      const existing = map.get(slot.date) || [];
      existing.push(slot);
      map.set(slot.date, existing);
    }
    map.forEach((daySlots) => {
      daySlots.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });
    return map;
  }, [slots]);

  // Build calendar grid
  const weeks = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const result: (Date | null)[][] = [];
    let currentWeek: (Date | null)[] = [];

    for (let i = 0; i < startDow; i++) {
      currentWeek.push(null);
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      currentWeek.push(new Date(year, month, d));
      if (currentWeek.length === 7) {
        result.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      result.push(currentWeek);
    }

    return result;
  }, [currentMonth]);

  const today = new Date();
  const todayKey = toDateKey(today);

  const handlePrevMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null);
    setSelectedWeekIndex(null);
  };

  const handleNextMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null);
    setSelectedWeekIndex(null);
  };

  const handleDayCellClick = (dateKey: string, weekIndex: number) => {
    if (selectedDate === dateKey) {
      setSelectedDate(null);
      setSelectedWeekIndex(null);
    } else {
      setSelectedDate(dateKey);
      setSelectedWeekIndex(weekIndex);
    }
  };

  const renderDayDetailStrip = () => {
    if (!selectedDate) return null;

    const daySlots = slotsByDate.get(selectedDate) || [];

    return (
      <div className="idash-month-detail-strip">
        <div className="strip-header">
          <span className="strip-date">{selectedDate}</span>
          <span className="strip-count">{daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}</span>
          <div className="strip-actions">
            <button className="strip-open-day" onClick={() => onDayClick(selectedDate)}>
              Open Day View
            </button>
            <button className="strip-close" onClick={() => { setSelectedDate(null); setSelectedWeekIndex(null); }}>
              &times;
            </button>
          </div>
        </div>
        {daySlots.length === 0 ? (
          <div className="strip-empty">No slots on this date.</div>
        ) : (
          <div className="strip-slots">
            {daySlots.map(slot => (
              <div key={slot.templateSlotId} className={`strip-slot status-${slot.status}`}>
                <span className="strip-slot-time">
                  {slot.startTime} - {slot.endTime}
                </span>
                <span className="strip-slot-topic">{slot.topicName || slot.templateName || '-'}</span>
                <span className={`strip-slot-status ${slot.status}`}>{slot.status}</span>
                {slot.attendanceRate !== null && (
                  <span className="strip-slot-attendance">{slot.attendanceRate}%</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="idash-month-view">
      {/* Month header */}
      <div className="idash-month-header">
        <button className="idash-nav-btn" onClick={handlePrevMonth}>&lsaquo;</button>
        <span className="idash-month-title">
          {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button className="idash-nav-btn" onClick={handleNextMonth}>&rsaquo;</button>
      </div>

      {/* Weekday headers */}
      <div className="idash-month-weekday-headers">
        {WEEKDAY_HEADERS.map(day => (
          <div key={day} className="idash-weekday-cell">{day}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="idash-month-grid">
        {weeks.map((week, weekIndex) => (
          <React.Fragment key={weekIndex}>
            <div className="idash-month-week-row">
              {week.map((day, dayIndex) => {
                if (!day) {
                  return <div key={dayIndex} className="idash-month-day empty" />;
                }

                const dateKey = toDateKey(day);
                const isToday = dateKey === todayKey;
                const daySlots = slotsByDate.get(dateKey) || [];
                const hasSlots = daySlots.length > 0;
                const isSelected = selectedDate === dateKey;
                const statusClass = getDayStatusColor(daySlots);

                const classes = [
                  'idash-month-day',
                  isToday && 'today',
                  hasSlots && 'has-slots',
                  isSelected && 'selected',
                  statusClass,
                ].filter(Boolean).join(' ');

                return (
                  <div
                    key={dayIndex}
                    className={classes}
                    onClick={() => handleDayCellClick(dateKey, weekIndex)}
                  >
                    <div className="day-num-row">
                      <span className="day-num">{day.getDate()}</span>
                      {hasSlots && <span className="day-slot-count">{daySlots.length}</span>}
                    </div>
                    {hasSlots && (
                      <div className="day-status-dots">
                        {daySlots.slice(0, 4).map((s, i) => (
                          <span key={i} className={`status-dot ${s.status}`} />
                        ))}
                        {daySlots.length > 4 && <span className="dot-more">+{daySlots.length - 4}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedWeekIndex === weekIndex && renderDayDetailStrip()}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default DashboardMonthView;
